import React, { useEffect, useRef, useState } from "react";
import { Polyline, Rectangle, useMap } from "react-leaflet";
import { useSelector } from "react-redux";

const ColoredGrid = React.memo(() => {
  const map = useMap();
  const [gridCells, setGridCells] = useState([]);
  const [diagonalLines, setDiagonalLines] = useState([]);
  const isUpdatingRef = useRef(false);

  const deliveryCenterLat = useSelector(
    (state) => state.deliveryCenter.deliveryCenter?.lat || 0
  );
  const deliveryCenterLng = useSelector(
    (state) => state.deliveryCenter.deliveryCenter?.lng || 0
  );

  const modernGridColors = [
    "rgba(165,168,250,0.3)",
    "rgba(88,90,148,0.3)",
    "rgba(88,90,148,0.3)",
    "rgba(165,168,250,0.3)",
  ];

  const calculatePointAtAngle = (centerLat, centerLng, angle, distance) => {
    const angleRad = (angle * Math.PI) / 180;
    const metersPerDegreeLat = 111320;
    const metersPerDegreeLng = 111320 * Math.cos((centerLat * Math.PI) / 180);

    const latOffset = (distance * Math.sin(angleRad)) / metersPerDegreeLat;
    const lngOffset = (distance * Math.cos(angleRad)) / metersPerDegreeLng;

    return [centerLat + latOffset, centerLng + lngOffset];
  };

  const calculateMaxDistance = (bounds, centerLat, centerLng) => {
    const north = bounds.getNorth();
    const south = bounds.getSouth();
    const east = bounds.getEast();
    const west = bounds.getWest();

    const latDiff = Math.max(
      Math.abs(north - centerLat),
      Math.abs(south - centerLat)
    );
    const lngDiff = Math.max(
      Math.abs(east - centerLng),
      Math.abs(west - centerLng)
    );

    const metersPerDegreeLat = 111320;
    const metersPerDegreeLng = 111320 * Math.cos((centerLat * Math.PI) / 180);

    const maxDistanceLat = latDiff * metersPerDegreeLat;
    const maxDistanceLng = lngDiff * metersPerDegreeLng;

    const finalDistance =
      Math.sqrt(
        maxDistanceLat * maxDistanceLat + maxDistanceLng * maxDistanceLng
      ) * 1.5;

    return finalDistance;
  };

  const calculateGridElements = () => {
    if (isUpdatingRef.current) {
      return;
    }

    isUpdatingRef.current = true;

    try {
      const bounds = map.getBounds();
      const north = bounds.getNorth();
      const south = bounds.getSouth();
      const east = bounds.getEast();
      const west = bounds.getWest();

      let centerLat = (north + south) / 2;
      let centerLng = (west + east) / 2;

      const hasValidDeliveryCenter =
        deliveryCenterLat &&
        deliveryCenterLng &&
        deliveryCenterLat !== 0 &&
        deliveryCenterLng !== 0;

      if (hasValidDeliveryCenter) {
        const dlat = parseFloat(deliveryCenterLat);
        const dlng = parseFloat(deliveryCenterLng);

        const isValidCoordinate =
          !isNaN(dlat) &&
          !isNaN(dlng) &&
          dlat <= north &&
          dlat >= south &&
          dlng <= east &&
          dlng >= west;

        if (isValidCoordinate) {
          centerLat = dlat;
          centerLng = dlng;
        }
      }

      const newGridCells = [
        {
          bounds: [
            [centerLat, west],
            [north, centerLng],
          ],
          color: modernGridColors[0],
        },
        {
          bounds: [
            [centerLat, centerLng],
            [north, east],
          ],
          color: modernGridColors[1],
        },
        {
          bounds: [
            [south, west],
            [centerLat, centerLng],
          ],
          color: modernGridColors[2],
        },
        {
          bounds: [
            [south, centerLng],
            [centerLat, east],
          ],
          color: modernGridColors[3],
        },
      ];

      const maxDistance = calculateMaxDistance(bounds, centerLat, centerLng);
      const angles = [45, 135, 225, 315];

      const newDiagonalLines = angles.map((angle) => {
        const endPoint = calculatePointAtAngle(
          centerLat,
          centerLng,
          angle,
          maxDistance
        );

        return {
          positions: [[centerLat, centerLng], endPoint],
          angle: angle,
        };
      });

      setGridCells(newGridCells);
      setDiagonalLines(newDiagonalLines);
    } catch (error) {
      console.error("Error calculating grid elements:", error);
      setGridCells([]);
      setDiagonalLines([]);
    } finally {
      isUpdatingRef.current = false;
    }
  };

  useEffect(() => {
    const timer = setTimeout(calculateGridElements, 100);

    const handleMapChange = () => {
      if (!isUpdatingRef.current) {
        window.requestAnimationFrame(calculateGridElements);
      }
    };

    map.on("zoomend", handleMapChange);
    map.on("moveend", handleMapChange);

    const cleanup = () => {
      clearTimeout(timer);
      map.off("zoomend", handleMapChange);
      map.off("moveend", handleMapChange);
    };

    return cleanup;
  }, [map]);

  useEffect(() => {
    if (!isUpdatingRef.current) {
      calculateGridElements();
    }
  }, [deliveryCenterLat, deliveryCenterLng]);

  const gridCellElements = gridCells.map((cell, index) => (
    <Rectangle
      key={`grid-cell-${index}`}
      bounds={cell.bounds}
      pathOptions={{
        color: "rgba(71, 85, 105, 0.4)",
        fillColor: cell.color,
        fillOpacity: 0.8,
        weight: 2,
        opacity: 0.6,
      }}
    />
  ));

  const diagonalLineElements = diagonalLines.map((line, index) => (
    <Polyline
      key={`diagonal-line-${index}`}
      positions={line.positions}
      pathOptions={{
        color: "rgba(53,55,143,0.5)",
        weight: 2,
        opacity: 0.9,
        dashArray: "8, 12",
      }}
    />
  ));

  return (
    <>
      {gridCellElements}
      {diagonalLineElements}
    </>
  );
});

ColoredGrid.displayName = "ColoredGrid";

export default ColoredGrid;
